import { useEffect, useState, useCallback } from 'react';
import { useOrganization } from '../contexts/OrganizationContext';
import {
  getFinancialYears, getFinancialYear, createFinancialYear, updateFinancialYear, deleteFinancialYear,
  closeFinancialYear, reopenFinancialYear, generateFinancialYearReport, downloadYearReport,
  createSupplierInvoice, uploadSupplierInvoiceFile, updateSupplierInvoice, deleteSupplierInvoice, downloadSupplierInvoiceFile,
  createOtherCost, uploadOtherCostFile, updateOtherCost, deleteOtherCost, downloadOtherCostFile,
} from '../services/ApiService';
import type { FinancialYearSummary, FinancialYearDetail, SupplierInvoice, OtherCost, PaymentPeriod } from '../types/models';
import Modal from '../components/Modal';
import {
  Wallet, Plus, ChevronDown, ChevronUp, Lock, Unlock, FileText, Download, RefreshCw,
  Trash2, Pencil, Receipt, Banknote, Users, Check, Clock, AlertTriangle,
} from 'lucide-react';

type ActiveTab = 'supplier_invoices' | 'other_costs' | 'member_payments' | 'year_report';

export default function EkonomiPage() {
  const { isBoardMember } = useOrganization();

  if (!isBoardMember) {
    return (
      <div className="max-w-2xl mx-auto p-6 lg:p-8 text-center py-16">
        <Wallet className="w-12 h-12 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-500">Du har inte behörighet att se ekonomisidan.</p>
      </div>
    );
  }

  return <EkonomiContent />;
}

function EkonomiContent() {
  const { currentOrg } = useOrganization();
  const [years, setYears] = useState<FinancialYearSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedYearId, setExpandedYearId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<ActiveTab>('supplier_invoices');
  const [detail, setDetail] = useState<FinancialYearDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [showYearModal, setShowYearModal] = useState(false);
  const [yearForm, setYearForm] = useState({ year: new Date().getFullYear().toString(), startDate: '', endDate: '', openingBalance: '0' });
  const [savingYear, setSavingYear] = useState(false);
  const [closing, setClosing] = useState(false);
  const [reopening, setReopening] = useState(false);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [deletingYear, setDeletingYear] = useState(false);

  const [showEditYearModal, setShowEditYearModal] = useState(false);
  const [editYearForm, setEditYearForm] = useState({ startDate: '', endDate: '' });
  const [savingEditYear, setSavingEditYear] = useState(false);

  const [showSupplierModal, setShowSupplierModal] = useState(false);
  const [supplierForm, setSupplierForm] = useState({ supplierName: '', description: '', amount: '', invoiceDate: '', dueDate: '' });
  const [supplierFile, setSupplierFile] = useState<File | null>(null);
  const [savingSupplier, setSavingSupplier] = useState(false);

  const [editingSupplier, setEditingSupplier] = useState<SupplierInvoice | null>(null);
  const [editSupplierForm, setEditSupplierForm] = useState({ supplierName: '', description: '', amount: '', invoiceDate: '', dueDate: '', isPaid: false });
  const [savingEditSupplier, setSavingEditSupplier] = useState(false);

  const [showCostModal, setShowCostModal] = useState(false);
  const [costForm, setCostForm] = useState({ description: '', category: '', amount: '', costDate: '' });
  const [costFile, setCostFile] = useState<File | null>(null);
  const [savingCost, setSavingCost] = useState(false);

  const [editingCost, setEditingCost] = useState<OtherCost | null>(null);
  const [editCostForm, setEditCostForm] = useState({ description: '', category: '', amount: '', costDate: '' });
  const [savingEditCost, setSavingEditCost] = useState(false);

  const fetchYears = useCallback(async () => {
    if (!currentOrg) return;
    try {
      const data = await getFinancialYears(currentOrg.id);
      setYears(data);
    } catch {
      setYears([]);
    } finally {
      setLoading(false);
    }
  }, [currentOrg]);

  useEffect(() => { fetchYears(); }, [fetchYears]);

  const fetchDetail = useCallback(async (yearId: string) => {
    if (!currentOrg) return;
    setDetailLoading(true);
    try {
      const data = await getFinancialYear(currentOrg.id, yearId);
      setDetail(data);
    } catch {
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  }, [currentOrg]);

  const handleExpandYear = (yearId: string) => {
    if (expandedYearId === yearId) {
      setExpandedYearId(null);
      setDetail(null);
    } else {
      setExpandedYearId(yearId);
      setActiveTab('supplier_invoices');
      fetchDetail(yearId);
    }
  };

  const formatDate = (d: string) => new Date(d).toLocaleDateString('sv-SE', { year: 'numeric', month: 'long', day: 'numeric' });
  const formatMoney = (n: number) => `${n.toLocaleString('sv-SE')} kr`;

  const yearTaken = years.some(y => y.year === parseInt(yearForm.year, 10));

  const handleCreateYear = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentOrg) return;
    setSavingYear(true);
    try {
      await createFinancialYear(currentOrg.id, {
        year: parseInt(yearForm.year, 10),
        startDate: yearForm.startDate,
        endDate: yearForm.endDate,
        openingBalance: parseFloat(yearForm.openingBalance || '0'),
      });
      setShowYearModal(false);
      setYearForm({ year: new Date().getFullYear().toString(), startDate: '', endDate: '', openingBalance: '0' });
      fetchYears();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Kunde inte skapa bokföringsåret.');
    } finally {
      setSavingYear(false);
    }
  };

  const handleCloseYear = async () => {
    if (!currentOrg || !expandedYearId) return;
    if (!window.confirm('Avsluta bokföringsåret? Detta genererar en årsrapport och låser året för redigering.')) return;
    setClosing(true);
    try {
      await closeFinancialYear(currentOrg.id, expandedYearId);
      await Promise.all([fetchYears(), fetchDetail(expandedYearId)]);
    } catch {
      alert('Kunde inte avsluta bokföringsåret.');
    } finally {
      setClosing(false);
    }
  };

  const handleDownloadReport = async (yearId: string, year: number) => {
    if (!currentOrg) return;
    try {
      await downloadYearReport(currentOrg.id, yearId, `arsrapport-${year}.pdf`);
    } catch {
      alert('Kunde inte hämta årsrapporten.');
    }
  };

  const handleGenerateReport = async () => {
    if (!currentOrg || !expandedYearId) return;
    setGeneratingReport(true);
    try {
      await generateFinancialYearReport(currentOrg.id, expandedYearId);
      await Promise.all([fetchYears(), fetchDetail(expandedYearId)]);
    } catch {
      alert('Kunde inte generera rapporten.');
    } finally {
      setGeneratingReport(false);
    }
  };

  const handleReopenYear = async () => {
    if (!currentOrg || !expandedYearId) return;
    if (!window.confirm('Lås upp bokföringsåret för redigering? Den befintliga årsrapporten tas bort och behöver genereras på nytt.')) return;
    setReopening(true);
    try {
      await reopenFinancialYear(currentOrg.id, expandedYearId);
      await Promise.all([fetchYears(), fetchDetail(expandedYearId)]);
    } catch {
      alert('Kunde inte låsa upp bokföringsåret.');
    } finally {
      setReopening(false);
    }
  };

  const handleDeleteYear = async (yearId: string) => {
    if (!currentOrg) return;
    const year = years.find(y => y.id === yearId);
    if (year?.status === 'closed') {
      alert('Kan inte ta bort ett avslutat bokföringsår.');
      return;
    }
    if (!window.confirm('Ta bort detta bokföringsår? Leverantörsfakturor och övriga kostnader raderas permanent. Kopplade betalningsperioder tas inte bort, men kopplingen till bokföringsåret försvinner.')) return;
    setDeletingYear(true);
    try {
      await deleteFinancialYear(currentOrg.id, yearId);
      if (expandedYearId === yearId) {
        setExpandedYearId(null);
        setDetail(null);
      }
      fetchYears();
    } catch {
      alert('Kunde inte ta bort bokföringsåret.');
    } finally {
      setDeletingYear(false);
    }
  };

  const openEditYear = () => {
    if (!detail) return;
    setEditYearForm({
      startDate: detail.summary.startDate.slice(0, 10),
      endDate: detail.summary.endDate.slice(0, 10),
    });
    setShowEditYearModal(true);
  };

  const handleEditYearSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentOrg || !expandedYearId || !detail) return;
    setSavingEditYear(true);
    try {
      await updateFinancialYear(currentOrg.id, expandedYearId, {
        startDate: editYearForm.startDate,
        endDate: editYearForm.endDate,
        openingBalance: detail.summary.openingBalance,
      });
      setShowEditYearModal(false);
      fetchDetail(expandedYearId);
      fetchYears();
    } catch {
      alert('Kunde inte spara bokföringsåret.');
    } finally {
      setSavingEditYear(false);
    }
  };

  const handleSaveOpeningBalance = async (value: number) => {
    if (!currentOrg || !expandedYearId || !detail) return;
    try {
      await updateFinancialYear(currentOrg.id, expandedYearId, {
        startDate: detail.summary.startDate,
        endDate: detail.summary.endDate,
        openingBalance: value,
      });
      fetchDetail(expandedYearId);
      fetchYears();
    } catch {
      alert('Kunde inte spara ingående balans.');
    }
  };

  const handleCreateSupplierInvoice = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentOrg || !expandedYearId) return;
    setSavingSupplier(true);
    try {
      const created = await createSupplierInvoice(currentOrg.id, expandedYearId, {
        supplierName: supplierForm.supplierName,
        description: supplierForm.description,
        amount: parseFloat(supplierForm.amount),
        invoiceDate: supplierForm.invoiceDate,
        dueDate: supplierForm.dueDate,
      });
      if (supplierFile) {
        await uploadSupplierInvoiceFile(currentOrg.id, expandedYearId, created.id, supplierFile);
      }
      setShowSupplierModal(false);
      setSupplierForm({ supplierName: '', description: '', amount: '', invoiceDate: '', dueDate: '' });
      setSupplierFile(null);
      fetchDetail(expandedYearId);
      fetchYears();
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Kunde inte spara leverantörsfakturan.');
    } finally {
      setSavingSupplier(false);
    }
  };

  const handleDeleteSupplierInvoice = async (id: string) => {
    if (!currentOrg || !expandedYearId) return;
    if (!window.confirm('Ta bort denna leverantörsfaktura?')) return;
    try {
      await deleteSupplierInvoice(currentOrg.id, expandedYearId, id);
      fetchDetail(expandedYearId);
      fetchYears();
    } catch {
      alert('Kunde inte ta bort fakturan.');
    }
  };

  const handleToggleSupplierPaid = async (invoice: SupplierInvoice) => {
    if (!currentOrg || !expandedYearId) return;
    try {
      await updateSupplierInvoice(currentOrg.id, expandedYearId, invoice.id, {
        supplierName: invoice.supplierName,
        description: invoice.description,
        amount: invoice.amount,
        invoiceDate: invoice.invoiceDate,
        dueDate: invoice.dueDate,
        isPaid: !invoice.isPaid,
      });
      fetchDetail(expandedYearId);
      fetchYears();
    } catch {
      alert('Kunde inte uppdatera betalstatus.');
    }
  };

  const openEditSupplier = (invoice: SupplierInvoice) => {
    setEditingSupplier(invoice);
    setEditSupplierForm({
      supplierName: invoice.supplierName,
      description: invoice.description,
      amount: String(invoice.amount),
      invoiceDate: invoice.invoiceDate.slice(0, 10),
      dueDate: invoice.dueDate.slice(0, 10),
      isPaid: invoice.isPaid,
    });
  };

  const handleEditSupplierSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentOrg || !expandedYearId || !editingSupplier) return;
    setSavingEditSupplier(true);
    try {
      await updateSupplierInvoice(currentOrg.id, expandedYearId, editingSupplier.id, {
        supplierName: editSupplierForm.supplierName,
        description: editSupplierForm.description,
        amount: parseFloat(editSupplierForm.amount),
        invoiceDate: editSupplierForm.invoiceDate,
        dueDate: editSupplierForm.dueDate,
        isPaid: editSupplierForm.isPaid,
      });
      setEditingSupplier(null);
      fetchDetail(expandedYearId);
      fetchYears();
    } catch {
      alert('Kunde inte spara leverantörsfakturan.');
    } finally {
      setSavingEditSupplier(false);
    }
  };

  const handleCreateOtherCost = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentOrg || !expandedYearId) return;
    setSavingCost(true);
    try {
      const created = await createOtherCost(currentOrg.id, expandedYearId, {
        description: costForm.description,
        category: costForm.category,
        amount: parseFloat(costForm.amount),
        costDate: costForm.costDate,
      });
      if (costFile) {
        await uploadOtherCostFile(currentOrg.id, expandedYearId, created.id, costFile);
      }
      setShowCostModal(false);
      setCostForm({ description: '', category: '', amount: '', costDate: '' });
      setCostFile(null);
      fetchDetail(expandedYearId);
      fetchYears();
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Kunde inte spara kostnaden.');
    } finally {
      setSavingCost(false);
    }
  };

  const handleDeleteOtherCost = async (id: string) => {
    if (!currentOrg || !expandedYearId) return;
    if (!window.confirm('Ta bort denna kostnad?')) return;
    try {
      await deleteOtherCost(currentOrg.id, expandedYearId, id);
      fetchDetail(expandedYearId);
      fetchYears();
    } catch {
      alert('Kunde inte ta bort kostnaden.');
    }
  };

  const openEditCost = (cost: OtherCost) => {
    setEditingCost(cost);
    setEditCostForm({
      description: cost.description,
      category: cost.category,
      amount: String(cost.amount),
      costDate: cost.costDate.slice(0, 10),
    });
  };

  const handleEditCostSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentOrg || !expandedYearId || !editingCost) return;
    setSavingEditCost(true);
    try {
      await updateOtherCost(currentOrg.id, expandedYearId, editingCost.id, {
        description: editCostForm.description,
        category: editCostForm.category,
        amount: parseFloat(editCostForm.amount),
        costDate: editCostForm.costDate,
      });
      setEditingCost(null);
      fetchDetail(expandedYearId);
      fetchYears();
    } catch {
      alert('Kunde inte spara kostnaden.');
    } finally {
      setSavingEditCost(false);
    }
  };

  if (loading) return <div className="p-8 text-center text-gray-500">Laddar...</div>;

  return (
    <div className="max-w-4xl mx-auto p-6 lg:p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Ekonomi</h1>
          <p className="text-gray-500 text-sm mt-1">Bokslut och ekonomisk översikt</p>
        </div>
        <button data-tour="year-add" onClick={() => setShowYearModal(true)} className="flex items-center gap-2 px-4 py-2.5 bg-teal-700 hover:bg-teal-800 text-white rounded-xl font-medium text-sm">
          <Plus className="w-4 h-4" />
          Nytt bokföringsår
        </button>
      </div>

      {years.length === 0 ? (
        <div className="text-center py-16">
          <Wallet className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">Inga bokföringsår skapade</p>
          <p className="text-gray-400 text-sm mt-1">Skapa ett nytt bokföringsår för att börja.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {years.map(year => {
            const expanded = expandedYearId === year.id;
            return (
              <div key={year.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div onClick={() => handleExpandYear(year.id)} className="w-full p-5 flex items-center justify-between cursor-pointer hover:bg-gray-50 transition-colors">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-teal-50 flex items-center justify-center">
                      <Wallet className="w-6 h-6 text-teal-700" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 text-lg">Räkenskapsår {year.year}</h3>
                      <p className="text-sm text-gray-500">
                        {formatDate(year.startDate)} – {formatDate(year.endDate)}
                        <span className={`ml-3 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${year.status === 'open' ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                          {year.status === 'open' ? 'Öppet' : 'Avslutat'}
                        </span>
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {year.status === 'open' && (
                      <button onClick={e => { e.stopPropagation(); handleDeleteYear(year.id); }} disabled={deletingYear} className="p-1.5 rounded-lg hover:bg-red-50 hover:text-red-500 transition-colors disabled:opacity-50">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                    {expanded ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                  </div>
                </div>

                {expanded && (
                  <div className="border-t border-gray-100">
                    {detailLoading || !detail ? (
                      <div className="p-8 text-center text-gray-500">Laddar...</div>
                    ) : (
                      <EkonomiYearDetail
                        year={year}
                        detail={detail}
                        currentOrgId={currentOrg!.id}
                        activeTab={activeTab}
                        setActiveTab={setActiveTab}
                        onEditYear={openEditYear}
                        onSaveOpeningBalance={handleSaveOpeningBalance}
                        onCloseYear={handleCloseYear}
                        onReopenYear={handleReopenYear}
                        onGenerateReport={handleGenerateReport}
                        onDownloadReport={() => handleDownloadReport(year.id, year.year)}
                        closing={closing}
                        reopening={reopening}
                        generatingReport={generatingReport}
                        onAddSupplier={() => setShowSupplierModal(true)}
                        onDeleteSupplier={handleDeleteSupplierInvoice}
                        onToggleSupplierPaid={handleToggleSupplierPaid}
                        onEditSupplier={openEditSupplier}
                        onDownloadSupplierFile={(id, name) => downloadSupplierInvoiceFile(currentOrg!.id, year.id, id, name)}
                        onAddCost={() => setShowCostModal(true)}
                        onDeleteCost={handleDeleteOtherCost}
                        onEditCost={openEditCost}
                        onDownloadCostFile={(id, name) => downloadOtherCostFile(currentOrg!.id, year.id, id, name)}
                        formatDate={formatDate}
                        formatMoney={formatMoney}
                      />
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Modal isOpen={showYearModal} onClose={() => setShowYearModal(false)} title="Nytt bokföringsår">
        <form onSubmit={handleCreateYear} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">År</label>
            <input type="number" value={yearForm.year} onChange={e => setYearForm({ ...yearForm, year: e.target.value })} required className={`w-full px-4 py-2.5 rounded-xl border focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none ${yearTaken ? 'border-red-300' : 'border-gray-200'}`} />
            {yearTaken && <p className="text-sm text-red-600 mt-1">Bokföringsår {yearForm.year} finns redan. Välj ett annat år.</p>}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Startdatum</label>
              <input type="date" value={yearForm.startDate} onChange={e => setYearForm({ ...yearForm, startDate: e.target.value })} required className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Slutdatum</label>
              <input type="date" value={yearForm.endDate} onChange={e => setYearForm({ ...yearForm, endDate: e.target.value })} required className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">På kontot vid ingången av året (SEK)</label>
            <input type="number" value={yearForm.openingBalance} onChange={e => setYearForm({ ...yearForm, openingBalance: e.target.value })} step="0.01" className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none" />
            <p className="text-xs text-gray-500 mt-1">Överförs normalt från föregående års utgående balans.</p>
          </div>
          <div className="flex gap-3 justify-end pt-2">
            <button type="button" onClick={() => setShowYearModal(false)} className="px-4 py-2 text-gray-600">Avbryt</button>
            <button type="submit" disabled={savingYear || yearTaken} className="px-6 py-2 bg-teal-700 hover:bg-teal-800 text-white rounded-xl font-medium disabled:opacity-50">
              {savingYear ? 'Skapar...' : 'Skapa'}
            </button>
          </div>
        </form>
      </Modal>

      <Modal isOpen={showEditYearModal} onClose={() => setShowEditYearModal(false)} title="Redigera bokföringsår">
        <form onSubmit={handleEditYearSave} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Startdatum</label>
              <input type="date" value={editYearForm.startDate} onChange={e => setEditYearForm({ ...editYearForm, startDate: e.target.value })} required className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Slutdatum</label>
              <input type="date" value={editYearForm.endDate} onChange={e => setEditYearForm({ ...editYearForm, endDate: e.target.value })} required className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none" />
            </div>
          </div>
          <div className="flex gap-3 justify-end pt-2">
            <button type="button" onClick={() => setShowEditYearModal(false)} className="px-4 py-2 text-gray-600">Avbryt</button>
            <button type="submit" disabled={savingEditYear} className="px-6 py-2 bg-teal-700 hover:bg-teal-800 text-white rounded-xl font-medium disabled:opacity-50">
              {savingEditYear ? 'Sparar...' : 'Spara'}
            </button>
          </div>
        </form>
      </Modal>

      <Modal isOpen={showSupplierModal} onClose={() => setShowSupplierModal(false)} title="Ny leverantörsfaktura">
        <form onSubmit={handleCreateSupplierInvoice} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Leverantör</label>
            <input type="text" value={supplierForm.supplierName} onChange={e => setSupplierForm({ ...supplierForm, supplierName: e.target.value })} required className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Beskrivning</label>
            <input type="text" value={supplierForm.description} onChange={e => setSupplierForm({ ...supplierForm, description: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Belopp (SEK)</label>
              <input type="number" value={supplierForm.amount} onChange={e => setSupplierForm({ ...supplierForm, amount: e.target.value })} required min="0" step="0.01" className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Fakturadatum</label>
              <input type="date" value={supplierForm.invoiceDate} onChange={e => setSupplierForm({ ...supplierForm, invoiceDate: e.target.value })} required className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Förfallodag</label>
            <input type="date" value={supplierForm.dueDate} onChange={e => setSupplierForm({ ...supplierForm, dueDate: e.target.value })} required className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Bifoga faktura (valfritt)</label>
            <input type="file" onChange={e => setSupplierFile(e.target.files?.[0] ?? null)} className="w-full text-sm" />
          </div>
          <div className="flex gap-3 justify-end pt-2">
            <button type="button" onClick={() => setShowSupplierModal(false)} className="px-4 py-2 text-gray-600">Avbryt</button>
            <button type="submit" disabled={savingSupplier} className="px-6 py-2 bg-teal-700 hover:bg-teal-800 text-white rounded-xl font-medium disabled:opacity-50">
              {savingSupplier ? 'Sparar...' : 'Spara'}
            </button>
          </div>
        </form>
      </Modal>

      <Modal isOpen={showCostModal} onClose={() => setShowCostModal(false)} title="Ny övrig kostnad">
        <form onSubmit={handleCreateOtherCost} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Beskrivning</label>
            <input type="text" value={costForm.description} onChange={e => setCostForm({ ...costForm, description: e.target.value })} required className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Kategori</label>
            <input type="text" value={costForm.category} onChange={e => setCostForm({ ...costForm, category: e.target.value })} required placeholder="t.ex. Underhåll, Försäkring" className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Belopp (SEK)</label>
              <input type="number" value={costForm.amount} onChange={e => setCostForm({ ...costForm, amount: e.target.value })} required min="0" step="0.01" className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Datum</label>
              <input type="date" value={costForm.costDate} onChange={e => setCostForm({ ...costForm, costDate: e.target.value })} required className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Bifoga underlag (valfritt)</label>
            <input type="file" onChange={e => setCostFile(e.target.files?.[0] ?? null)} className="w-full text-sm" />
          </div>
          <div className="flex gap-3 justify-end pt-2">
            <button type="button" onClick={() => setShowCostModal(false)} className="px-4 py-2 text-gray-600">Avbryt</button>
            <button type="submit" disabled={savingCost} className="px-6 py-2 bg-teal-700 hover:bg-teal-800 text-white rounded-xl font-medium disabled:opacity-50">
              {savingCost ? 'Sparar...' : 'Spara'}
            </button>
          </div>
        </form>
      </Modal>

      <Modal isOpen={!!editingSupplier} onClose={() => setEditingSupplier(null)} title="Redigera leverantörsfaktura">
        <form onSubmit={handleEditSupplierSave} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Leverantör</label>
            <input type="text" value={editSupplierForm.supplierName} onChange={e => setEditSupplierForm({ ...editSupplierForm, supplierName: e.target.value })} required className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Beskrivning</label>
            <input type="text" value={editSupplierForm.description} onChange={e => setEditSupplierForm({ ...editSupplierForm, description: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Belopp (SEK)</label>
              <input type="number" value={editSupplierForm.amount} onChange={e => setEditSupplierForm({ ...editSupplierForm, amount: e.target.value })} required min="0" step="0.01" className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Fakturadatum</label>
              <input type="date" value={editSupplierForm.invoiceDate} onChange={e => setEditSupplierForm({ ...editSupplierForm, invoiceDate: e.target.value })} required className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Förfallodag</label>
            <input type="date" value={editSupplierForm.dueDate} onChange={e => setEditSupplierForm({ ...editSupplierForm, dueDate: e.target.value })} required className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none" />
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={editSupplierForm.isPaid} onChange={e => setEditSupplierForm({ ...editSupplierForm, isPaid: e.target.checked })} className="rounded text-teal-600 focus:ring-teal-500" />
            Betald
          </label>
          <div className="flex gap-3 justify-end pt-2">
            <button type="button" onClick={() => setEditingSupplier(null)} className="px-4 py-2 text-gray-600">Avbryt</button>
            <button type="submit" disabled={savingEditSupplier} className="px-6 py-2 bg-teal-700 hover:bg-teal-800 text-white rounded-xl font-medium disabled:opacity-50">
              {savingEditSupplier ? 'Sparar...' : 'Spara'}
            </button>
          </div>
        </form>
      </Modal>

      <Modal isOpen={!!editingCost} onClose={() => setEditingCost(null)} title="Redigera övrig kostnad">
        <form onSubmit={handleEditCostSave} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Beskrivning</label>
            <input type="text" value={editCostForm.description} onChange={e => setEditCostForm({ ...editCostForm, description: e.target.value })} required className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Kategori</label>
            <input type="text" value={editCostForm.category} onChange={e => setEditCostForm({ ...editCostForm, category: e.target.value })} required className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Belopp (SEK)</label>
              <input type="number" value={editCostForm.amount} onChange={e => setEditCostForm({ ...editCostForm, amount: e.target.value })} required min="0" step="0.01" className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Datum</label>
              <input type="date" value={editCostForm.costDate} onChange={e => setEditCostForm({ ...editCostForm, costDate: e.target.value })} required className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none" />
            </div>
          </div>
          <div className="flex gap-3 justify-end pt-2">
            <button type="button" onClick={() => setEditingCost(null)} className="px-4 py-2 text-gray-600">Avbryt</button>
            <button type="submit" disabled={savingEditCost} className="px-6 py-2 bg-teal-700 hover:bg-teal-800 text-white rounded-xl font-medium disabled:opacity-50">
              {savingEditCost ? 'Sparar...' : 'Spara'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

function EkonomiYearDetail({
  year, detail, activeTab, setActiveTab, onEditYear, onSaveOpeningBalance, onCloseYear, onReopenYear,
  onGenerateReport, onDownloadReport, closing, reopening, generatingReport,
  onAddSupplier, onDeleteSupplier, onToggleSupplierPaid, onEditSupplier, onDownloadSupplierFile,
  onAddCost, onDeleteCost, onEditCost, onDownloadCostFile,
  formatDate, formatMoney,
}: {
  year: FinancialYearSummary;
  detail: FinancialYearDetail;
  currentOrgId: string;
  activeTab: ActiveTab;
  setActiveTab: (t: ActiveTab) => void;
  onEditYear: () => void;
  onSaveOpeningBalance: (value: number) => void;
  onCloseYear: () => void;
  onReopenYear: () => void;
  onGenerateReport: () => void;
  onDownloadReport: () => void;
  closing: boolean;
  reopening: boolean;
  generatingReport: boolean;
  onAddSupplier: () => void;
  onDeleteSupplier: (id: string) => void;
  onToggleSupplierPaid: (invoice: SupplierInvoice) => void;
  onEditSupplier: (invoice: SupplierInvoice) => void;
  onDownloadSupplierFile: (id: string, name: string) => void;
  onAddCost: () => void;
  onDeleteCost: (id: string) => void;
  onEditCost: (cost: OtherCost) => void;
  onDownloadCostFile: (id: string, name: string) => void;
  formatDate: (d: string) => string;
  formatMoney: (n: number) => string;
}) {
  const summary = detail.summary;
  const isClosed = summary.status === 'closed';
  const isBoardActionable = !isClosed;
  const supplierTotal = detail.supplierInvoices.reduce((s, i) => s + i.amount, 0);
  const otherCostTotal = detail.otherCosts.reduce((s, c) => s + c.amount, 0);
  const invoicedTotal = summary.totalIncome + summary.totalReceivables;

  return (
    <div>
      {isClosed && (
        <div className="bg-amber-50 border-b border-amber-100 px-5 py-3 flex items-center gap-2 text-amber-800 text-sm">
          <Lock className="w-4 h-4" />
          <span className="font-medium">Räkenskapsår avslutat</span>
          <span className="text-amber-600">— redigering är låst. Avslutat {summary.closedAt ? formatDate(summary.closedAt) : ''}</span>
        </div>
      )}

      <div className="px-5 pt-5 pb-2 bg-gray-50/50">
        <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">På kontot vid ingången av året</p>
              <p className="text-xs text-gray-400 mt-0.5">Ingående saldo</p>
            </div>
            {isBoardActionable && (
              <button onClick={onEditYear} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400" title="Redigera datum">
                <Pencil className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          {isBoardActionable ? (
            <input
              type="number"
              defaultValue={summary.openingBalance}
              onBlur={e => onSaveOpeningBalance(parseFloat(e.target.value) || 0)}
              className="w-40 text-right text-lg font-bold text-gray-900 px-3 py-1.5 rounded-lg border border-gray-200 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none"
            />
          ) : (
            <p className="text-xl font-bold text-gray-900">{formatMoney(summary.openingBalance)}</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 p-5 bg-gray-50/50">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Leverantörsfakturor</p>
          <p className="text-xl font-bold text-gray-900 mt-1">{formatMoney(supplierTotal)}</p>
          <p className="text-xs text-gray-400 mt-0.5">{detail.supplierInvoices.length} st</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Övriga kostnader</p>
          <p className="text-xl font-bold text-gray-900 mt-1">{formatMoney(otherCostTotal)}</p>
          <p className="text-xs text-gray-400 mt-0.5">{detail.otherCosts.length} st</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Medlemsavgifter</p>
          <p className="text-xl font-bold text-teal-700 mt-1">{formatMoney(summary.totalIncome)} <span className="text-sm font-normal text-gray-400">/ {formatMoney(invoicedTotal)}</span></p>
          <p className="text-xs text-gray-400 mt-0.5">{detail.paymentPeriods.length} perioder</p>
        </div>
        <div className={`rounded-xl border p-4 ${summary.totalReceivables > 0 ? 'bg-amber-50 border-amber-200' : 'bg-white border-gray-200'}`}>
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Fordringar</p>
          <p className={`text-xl font-bold mt-1 ${summary.totalReceivables > 0 ? 'text-amber-700' : 'text-gray-900'}`}>{formatMoney(summary.totalReceivables)}</p>
          <p className="text-xs text-gray-400 mt-0.5">Ej inbetalda avgifter</p>
        </div>
      </div>

      <div className="flex border-b border-gray-200 px-5 overflow-x-auto">
        <button
          onClick={() => setActiveTab('supplier_invoices')}
          className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === 'supplier_invoices' ? 'border-teal-700 text-teal-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
        >
          <Receipt className="w-4 h-4" />
          Leverantörsfakturor
        </button>
        <button
          onClick={() => setActiveTab('other_costs')}
          className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === 'other_costs' ? 'border-teal-700 text-teal-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
        >
          <Banknote className="w-4 h-4" />
          Övriga kostnader
        </button>
        <button
          onClick={() => setActiveTab('member_payments')}
          className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === 'member_payments' ? 'border-teal-700 text-teal-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
        >
          <Users className="w-4 h-4" />
          Betalningar från medlemmar
        </button>
        <button
          onClick={() => setActiveTab('year_report')}
          className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === 'year_report' ? 'border-teal-700 text-teal-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
        >
          <FileText className="w-4 h-4" />
          Årsredovisning
        </button>
      </div>

      <div className="p-5">
        {activeTab === 'supplier_invoices' && (
          <SupplierInvoicesTab
            invoices={detail.supplierInvoices}
            isBoardMember={isBoardActionable}
            onAdd={onAddSupplier}
            onDelete={onDeleteSupplier}
            onTogglePaid={onToggleSupplierPaid}
            onEdit={onEditSupplier}
            onDownloadFile={onDownloadSupplierFile}
            formatDate={formatDate}
            formatMoney={formatMoney}
          />
        )}
        {activeTab === 'other_costs' && (
          <OtherCostsTab
            costs={detail.otherCosts}
            isBoardMember={isBoardActionable}
            onAdd={onAddCost}
            onDelete={onDeleteCost}
            onEdit={onEditCost}
            onDownloadFile={onDownloadCostFile}
            formatDate={formatDate}
            formatMoney={formatMoney}
          />
        )}
        {activeTab === 'member_payments' && (
          <MemberPaymentsTab periods={detail.paymentPeriods} formatMoney={formatMoney} />
        )}
        {activeTab === 'year_report' && (
          <YearReportTab
            summary={summary}
            supplierTotal={supplierTotal}
            otherCostTotal={otherCostTotal}
            invoicedTotal={invoicedTotal}
            isClosed={isClosed}
            generatingReport={generatingReport}
            closing={closing}
            reopening={reopening}
            onGenerateReport={onGenerateReport}
            onDownloadReport={onDownloadReport}
            onCloseYear={onCloseYear}
            onReopenYear={onReopenYear}
            formatMoney={formatMoney}
            formatDate={formatDate}
          />
        )}
      </div>
    </div>
  );
}

function SupplierInvoicesTab({ invoices, isBoardMember, onAdd, onDelete, onTogglePaid, onEdit, onDownloadFile, formatDate, formatMoney }: {
  invoices: SupplierInvoice[];
  isBoardMember: boolean;
  onAdd: () => void;
  onDelete: (id: string) => void;
  onTogglePaid: (invoice: SupplierInvoice) => void;
  onEdit: (invoice: SupplierInvoice) => void;
  onDownloadFile: (id: string, name: string) => void;
  formatDate: (d: string) => string;
  formatMoney: (n: number) => string;
}) {
  return (
    <div className="space-y-3">
      {isBoardMember && (
        <div className="flex justify-end">
          <button data-tour="supplier-add" onClick={onAdd} className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-teal-700 hover:bg-teal-50 rounded-lg transition-colors">
            <Plus className="w-4 h-4" />
            Lägg till faktura
          </button>
        </div>
      )}
      {invoices.length === 0 ? (
        <p className="text-gray-500 text-sm text-center py-6">Inga leverantörsfakturor ännu.</p>
      ) : (
        <div className="divide-y divide-gray-100 border border-gray-200 rounded-xl overflow-hidden">
          {invoices.map(inv => (
            <div key={inv.id} className="px-4 py-3 flex items-center justify-between bg-white hover:bg-gray-50/50 transition-colors">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-gray-900 truncate">{inv.supplierName}</p>
                  {inv.isPaid && <span className="inline-flex items-center gap-0.5 text-xs font-medium text-green-700 bg-green-50 px-1.5 py-0.5 rounded-full"><Check className="w-3 h-3" /> Betald</span>}
                  {inv.fileDownloadUrl && (
                    <button onClick={() => onDownloadFile(inv.id, `${inv.supplierName}.pdf`)} className="inline-flex items-center gap-0.5 text-xs font-medium text-teal-700 bg-teal-50 px-1.5 py-0.5 rounded-full hover:bg-teal-100 transition-colors" title="Ladda ner bifogad fil">
                    <FileText className="w-3 h-3" /> PDF
                    </button>
                  )}
                </div>
                {inv.description && <p className="text-xs text-gray-500 truncate mt-0.5">{inv.description}</p>}
                <p className="text-xs text-gray-400 mt-0.5">
                  Fakturadatum: {formatDate(inv.invoiceDate)} | Förfallodag: {formatDate(inv.dueDate)}
                </p>
              </div>
              <div className="flex items-center gap-3 ml-4">
                <span className="text-sm font-semibold text-gray-900 whitespace-nowrap">{formatMoney(inv.amount)}</span>
                {isBoardMember && (
                  <div className="flex items-center gap-1">
                    <button onClick={() => onTogglePaid(inv)} className={`p-1.5 rounded-lg transition-colors ${inv.isPaid ? 'text-green-600 hover:bg-green-50' : 'text-gray-400 hover:bg-gray-100'}`} title={inv.isPaid ? 'Markera obetald' : 'Markera betald'}>
                      {inv.isPaid ? <Check className="w-4 h-4" /> : <Clock className="w-4 h-4" />}
                    </button>
                    <button onClick={() => onEdit(inv)} className="p-1.5 rounded-lg text-gray-400 hover:bg-teal-50 hover:text-teal-700 transition-colors" title="Redigera">
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => onDelete(inv.id)} className="p-1.5 rounded-lg text-gray-400 hover:bg-red-50 hover:text-red-500 transition-colors" title="Ta bort">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function OtherCostsTab({ costs, isBoardMember, onAdd, onDelete, onEdit, onDownloadFile, formatDate, formatMoney }: {
  costs: OtherCost[];
  isBoardMember: boolean;
  onAdd: () => void;
  onDelete: (id: string) => void;
  onEdit: (cost: OtherCost) => void;
  onDownloadFile: (id: string, name: string) => void;
  formatDate: (d: string) => string;
  formatMoney: (n: number) => string;
}) {
  return (
    <div className="space-y-3">
      {isBoardMember && (
        <div className="flex justify-end">
          <button data-tour="cost-add" onClick={onAdd} className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-teal-700 hover:bg-teal-50 rounded-lg transition-colors">
            <Plus className="w-4 h-4" />
            Lägg till kostnad
          </button>
        </div>
      )}
      {costs.length === 0 ? (
        <p className="text-gray-500 text-sm text-center py-6">Inga övriga kostnader ännu.</p>
      ) : (
        <div className="divide-y divide-gray-100 border border-gray-200 rounded-xl overflow-hidden">
          {costs.map(cost => (
            <div key={cost.id} className="px-4 py-3 flex items-center justify-between bg-white hover:bg-gray-50/50 transition-colors">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-gray-900 truncate">{cost.description}</p>
                  {cost.fileDownloadUrl && (
                    <button onClick={() => onDownloadFile(cost.id, `${cost.description}.pdf`)} className="inline-flex items-center gap-0.5 text-xs font-medium text-teal-700 bg-teal-50 px-1.5 py-0.5 rounded-full hover:bg-teal-100 transition-colors" title="Ladda ner bifogad fil">
                    <FileText className="w-3 h-3" /> PDF
                    </button>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                  {cost.category && <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{cost.category}</span>}
                  <span className="text-xs text-gray-400">{formatDate(cost.costDate)}</span>
                </div>
              </div>
              <div className="flex items-center gap-3 ml-4">
                <span className="text-sm font-semibold text-gray-900 whitespace-nowrap">{formatMoney(cost.amount)}</span>
                {isBoardMember && (
                  <div className="flex items-center gap-1">
                    <button onClick={() => onEdit(cost)} className="p-1.5 rounded-lg text-gray-400 hover:bg-teal-50 hover:text-teal-700 transition-colors" title="Redigera">
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => onDelete(cost.id)} className="p-1.5 rounded-lg text-gray-400 hover:bg-red-50 hover:text-red-500 transition-colors" title="Ta bort">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MemberPaymentsTab({ periods, formatMoney }: {
  periods: PaymentPeriod[];
  formatMoney: (n: number) => string;
}) {
  if (periods.length === 0) {
    return <p className="text-gray-500 text-sm text-center py-8">Inga betalningsperioder under detta räkenskapsår.</p>;
  }

  return (
    <div className="divide-y divide-gray-100 border border-gray-200 rounded-xl overflow-hidden">
      {periods.map(p => {
        const totalAmount = p.memberPayments.reduce((s, mp) => s + mp.amount, 0);
        const paidAmount = p.memberPayments.filter(mp => mp.isPaid).reduce((s, mp) => s + mp.amount, 0);
        const paidCount = p.memberPayments.filter(mp => mp.isPaid).length;
        const totalCount = p.memberPayments.length;
        return (
          <div key={p.id} className="px-4 py-3 flex items-center justify-between bg-white">
            <div>
              <p className="text-sm font-medium text-gray-900">{p.title}</p>
              <p className="text-xs text-gray-500 mt-0.5">
                <span className={paidCount === totalCount && totalCount > 0 ? 'text-green-600' : 'text-amber-600'}>
                  {paidCount}/{totalCount} betalda
                </span>
              </p>
            </div>
            <div className="text-right">
              <span className="text-sm font-semibold text-gray-900">{formatMoney(paidAmount)} / {formatMoney(totalAmount)}</span>
              <p className="text-xs text-gray-400 mt-0.5">betalt / fakturerat</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function YearReportTab({
  summary, supplierTotal, otherCostTotal, invoicedTotal, isClosed, generatingReport, closing, reopening,
  onGenerateReport, onDownloadReport, onCloseYear, onReopenYear, formatMoney, formatDate,
}: {
  summary: FinancialYearSummary;
  supplierTotal: number;
  otherCostTotal: number;
  invoicedTotal: number;
  isClosed: boolean;
  generatingReport: boolean;
  closing: boolean;
  reopening: boolean;
  onGenerateReport: () => void;
  onDownloadReport: () => void;
  onCloseYear: () => void;
  onReopenYear: () => void;
  formatMoney: (n: number) => string;
  formatDate: (d: string) => string;
}) {
  const netResult = summary.totalIncome - summary.totalExpenses;
  const closingBalance = summary.balance;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Ingående saldo</p>
          <p className="text-xl font-bold text-gray-900 mt-1">{formatMoney(summary.openingBalance)}</p>
        </div>
        <div className={`rounded-xl border-2 p-4 text-center ${netResult >= 0 ? 'border-green-200 bg-green-50/50' : 'border-red-200 bg-red-50/50'}`}>
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Årets resultat</p>
          <p className={`text-xl font-bold mt-1 ${netResult >= 0 ? 'text-green-700' : 'text-red-700'}`}>
            {netResult >= 0 ? '+' : ''}{formatMoney(netResult)}
          </p>
        </div>
        <div className={`rounded-xl border-2 p-4 text-center ${closingBalance >= 0 ? 'border-teal-200 bg-teal-50/50' : 'border-red-200 bg-red-50/50'}`}>
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Utgående saldo</p>
          <p className={`text-xl font-bold mt-1 ${closingBalance >= 0 ? 'text-teal-700' : 'text-red-700'}`}>{formatMoney(closingBalance)}</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-teal-500" />
            Intäkter
          </h4>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Medlemsavgifter (inbetalda)</span>
              <span className="font-medium text-gray-900">{formatMoney(summary.totalIncome)}</span>
            </div>
            <div className="flex justify-between text-sm text-gray-400">
              <span>Fakturerat totalt</span>
              <span>{formatMoney(invoicedTotal)}</span>
            </div>
            <div className="border-t border-gray-100 pt-2 flex justify-between text-sm font-semibold">
              <span className="text-gray-700">Totalt intäkter</span>
              <span className="text-teal-700">{formatMoney(summary.totalIncome)}</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-red-500" />
            Kostnader
          </h4>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Leverantörsfakturor</span>
              <span className="font-medium text-gray-900">{formatMoney(supplierTotal)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Övriga kostnader</span>
              <span className="font-medium text-gray-900">{formatMoney(otherCostTotal)}</span>
            </div>
            <div className="border-t border-gray-100 pt-2 flex justify-between text-sm font-semibold">
              <span className="text-gray-700">Totalt kostnader</span>
              <span className="text-red-700">{formatMoney(summary.totalExpenses)}</span>
            </div>
          </div>
        </div>
      </div>

      {summary.totalReceivables > 0 && (
        <div className="bg-amber-50 rounded-xl border border-amber-200 p-5">
          <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-amber-500" />
            Fordringar
          </h4>
          <p className="text-xs text-amber-700 mb-3">
            Belopp som fakturerats till medlemmar men ännu ej betalats vid årets slut.
          </p>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Fakturerat totalt</span>
              <span className="font-medium text-gray-900">{formatMoney(invoicedTotal)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Inbetalt</span>
              <span className="font-medium text-gray-900">{formatMoney(summary.totalIncome)}</span>
            </div>
            <div className="border-t border-amber-200 pt-2 flex justify-between text-sm font-semibold">
              <span className="text-gray-700">Utestående fordringar</span>
              <span className="text-amber-700">{formatMoney(summary.totalReceivables)}</span>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-teal-50 flex items-center justify-center">
              <FileText className="w-5 h-5 text-teal-700" />
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900">Årsredovisning (PDF)</p>
              <p className="text-xs text-gray-500 mt-0.5">
                {summary.reportDownloadUrl
                  ? 'Rapport genererad — klicka för att ladda ner'
                  : 'Ingen rapport genererad ännu'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {summary.reportDownloadUrl && (
              <button onClick={onDownloadReport} className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-teal-700 hover:bg-teal-50 rounded-lg transition-colors">
                <Download className="w-4 h-4" />
                Ladda ner
              </button>
            )}
            {!isClosed && (
              <button
                onClick={onGenerateReport}
                disabled={generatingReport}
                className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium bg-teal-700 hover:bg-teal-800 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${generatingReport ? 'animate-spin' : ''}`} />
                {generatingReport ? 'Genererar...' : summary.reportDownloadUrl ? 'Generera om' : 'Generera rapport'}
              </button>
            )}
          </div>
        </div>
      </div>

      {!isClosed && (
        <div className="bg-gray-50 rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <p className="text-sm font-semibold text-gray-900">Avsluta räkenskapsår</p>
              <p className="text-xs text-gray-500 mt-0.5">
                När du avslutar låses alla poster och en slutgiltig årsredovisning genereras.
              </p>
            </div>
            <button
              onClick={onCloseYear}
              disabled={closing}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50"
            >
              <Lock className="w-4 h-4" />
              {closing ? 'Avslutar...' : 'Avsluta och lås'}
            </button>
          </div>
        </div>
      )}

      {isClosed && (
        <div className="bg-green-50 rounded-xl border border-green-200 p-5">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <Check className="w-5 h-5 text-green-600" />
              <div>
                <p className="text-sm font-medium text-green-800">Räkenskapsår avslutat</p>
                <p className="text-xs text-green-600 mt-0.5">
                  Avslutades {summary.closedAt ? formatDate(summary.closedAt) : ''}
                </p>
              </div>
            </div>
            <button
              onClick={onReopenYear}
              disabled={reopening}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-amber-100 hover:bg-amber-200 text-amber-800 rounded-lg transition-colors disabled:opacity-50"
            >
              <Unlock className="w-4 h-4" />
              {reopening ? 'Låser upp...' : 'Lås upp'}
            </button>
          </div>
          <div className="mt-3 pt-3 border-t border-green-100 flex items-start gap-2 text-xs text-green-700">
            <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
            <span>Att låsa upp tar bort den befintliga årsredovisningen. En ny behöver genereras när du avslutar igen.</span>
          </div>
        </div>
      )}
    </div>
  );
}
